import React, { useContext } from 'react';

import { Box, Button, Tooltip, Typography } from '@material-ui/core';

import { searchInviteWorkersRequest } from '@vyce/core/src/api/invites';
import { AppChip } from '@vyce/core/src/components/AppChip';
import { AppSearchInput } from '@vyce/core/src/components/inputs';
import { WorkersScrollList } from '@vyce/core/src/components/WorkersScrollList';
import { UniversalInviteContext } from '@vyce/core/src/contexts';
import { useCompanyWorkersList } from '@vyce/core/src/hooks/useCompanyWorkersList';

import { INVITE_MODULES_CONFIG, InviteModuleConfig } from '../constants';
import { InviteSelectionApi } from '../hooks/useInviteSelection';
import useStyles from '../styles';
import { ResourceOption, WorkersFilter } from '../types';
import { getBulkLabel, pluralize } from '../utils/labels';
import { ExistingUserRow } from './ExistingUserRow';
import { ExistingUsersFilters } from './ExistingUsersFilters';

interface Props {
  companyId: string;
  filter: WorkersFilter;
  onFilterChange: (filter: WorkersFilter) => void;
  filterModules: InviteModuleConfig[];
  resourceOptions: ResourceOption[];
  selection: InviteSelectionApi;
}

export const ExistingUsersPanel: React.FC<Props> = ({
  companyId,
  filter,
  onFilterChange,
  filterModules,
  resourceOptions,
  selection,
}) => {
  const classes = useStyles();
  const { fetchWorkers = searchInviteWorkersRequest } = useContext(UniversalInviteContext);
  const config = filter.module ? INVITE_MODULES_CONFIG[filter.module] : null;
  const { search, setSearch, workers, total, loading, loadingMore, loadMore } = useCompanyWorkersList({
    companyId,
    modules: config ? [config.apiModuleName] : undefined,
    resourceIds: filter.resourceId ? [filter.resourceId] : undefined,
    request: fetchWorkers,
  });

  const { bulk } = selection.state;
  const covered = selection.isCovered(filter);
  const searchActive = search.trim().length > 0;

  // "Select all" maps to the backend `source`, which knows resources and "everyone" — not a
  // search string and not a module on its own
  let selectAllBlockedReason: string | null = null;
  if (!covered) {
    if (searchActive) {
      selectAllBlockedReason = 'Clear the search to select everyone in this list';
    } else if (config && !filter.resourceId) {
      selectAllBlockedReason = `Pick a ${config.resourceLabel} to select everyone in it`;
    } else if (!total) {
      selectAllBlockedReason = 'No users to select';
    }
  }

  const handleSelectAll = () => {
    if (covered) {
      selection.clearBulk();
      return;
    }
    if (filter.module && filter.resourceId && config) {
      const label =
        resourceOptions.find(option => option.id === filter.resourceId)?.name ?? config.resourceLabel;
      selection.selectAll({
        kind: 'resource',
        module: filter.module,
        resourceId: filter.resourceId,
        label,
        count: total,
      });
      return;
    }
    selection.selectAll({ kind: 'all', count: total });
  };

  if (!companyId) {
    return <Typography color="textSecondary">Select a company to see its users.</Typography>;
  }

  return (
    <Box display="flex" flexDirection="column" gridGap={12}>
      <ExistingUsersFilters
        filter={filter}
        onChange={onFilterChange}
        modules={filterModules}
        resourceOptions={resourceOptions}
      />

      <AppSearchInput
        value={search}
        onChange={event => setSearch(event.target.value)}
        placeholder="Search by name..."
        isSmall
        smallRounding
        isBorder
        expanded
        fullWidth
      />

      <Box className={classes.countRow}>
        <Typography className={classes.hint}>{loading ? 'Loading...' : pluralize(total, 'user')}</Typography>

        <Tooltip title={selectAllBlockedReason ?? ''} disableHoverListener={!selectAllBlockedReason}>
          <span>
            <Button
              size="small"
              color="primary"
              className={classes.linkButton}
              disabled={loading || !!selectAllBlockedReason}
              onClick={handleSelectAll}
            >
              {covered ? 'Deselect all' : 'Select all'}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* A scope picked under another filter stays active — keep it visible and removable */}
      {bulk && !covered && (
        <Box display="flex" alignItems="center" gridGap={8}>
          <AppChip label={getBulkLabel(bulk)} onDelete={selection.clearBulk} />
        </Box>
      )}

      <WorkersScrollList
        workers={workers}
        loading={loading}
        loadingMore={loadingMore}
        onLoadMore={loadMore}
        height="40vh"
        emptyText="No users match these filters."
        renderRow={worker => (
          <ExistingUserRow
            key={worker.user_id}
            worker={worker}
            checked={selection.isChecked(worker, filter)}
            onToggle={() => selection.toggleWorker(worker, filter)}
          />
        )}
      />
    </Box>
  );
};
